import * as THREE from 'three';
import { colored, jitter } from '../../Style.js';

// Sunlit lagoon: bleached sand, weathered timber, mossy stone and bright
// painted buoys, all flat-shaded to sit with the other low-poly themes.
export const SEA = {
  sand: 0xead7a4,
  sandDark: 0xd2b984,
  sandWet: 0xbfa575,
  grass: 0x6aa84a,
  grassDark: 0x4f8a38,
  rock: 0x8a8076,
  rockDark: 0x635a52,
  rockLight: 0xa79e92,
  stone: 0xb0a898,
  stoneDark: 0x857d70,
  seaweed: 0x3f6a32,
  barnacle: 0xe4ddcc,
  wood: 0x8a5a34,
  woodDark: 0x5e3b22,
  woodLight: 0xb88752,
  driftwood: 0xa8977e,
  rope: 0xd1bd8c,
  iron: 0x3b3c40,
  canvas: 0xf3ecd9,
  white: 0xf7f4ec,
  red: 0xd8382e,
  hull: 0x3f4a56,
  palmTrunk: 0x9a7650,
  palmLeaf: 0x3f9a3a,
  palmLeafDark: 0x2f7a30,
  lamp: 0xffe6a0
};

export const NEUTRAL = '#e8e4da';

// One clock for every animated naval material (foam, water, beams)
export const seaTime = { value: 0 };

// Stable hash of a string id, for seeding per-object variation.
export function hashId(id) {
  let h = 2166136261;
  const str = String(id);
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

let foamMaterial = null;

// Breaking surf: a band of white that churns slowly and thins away from the shore.
// Geometry carries aEdge (0 at the shore, 1 at the outer edge).
export function getFoamMaterial() {
  if (foamMaterial) return foamMaterial;
  foamMaterial = new THREE.ShaderMaterial({
    uniforms: { uTime: seaTime },
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide,
    polygonOffset: true,
    polygonOffsetFactor: -2,
    vertexShader: /* glsl */`
      attribute float aEdge;
      varying float vEdge;
      varying vec2 vWorld;
      void main() {
        vEdge = aEdge;
        vec4 world = modelMatrix * vec4(position, 1.0);
        vWorld = world.xz;
        gl_Position = projectionMatrix * viewMatrix * world;
      }
    `,
    fragmentShader: /* glsl */`
      uniform float uTime;
      varying float vEdge;
      varying vec2 vWorld;
      float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
      float vnoise(vec2 p) {
        vec2 i = floor(p);
        vec2 f = fract(p);
        vec2 u = f * f * (3.0 - 2.0 * f);
        return mix(mix(hash(i), hash(i + vec2(1.0, 0.0)), u.x), mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x), u.y);
      }
      void main() {
        float n = vnoise(vWorld * 0.9 + vec2(uTime * 0.3, -uTime * 0.2)) * 0.6 + vnoise(vWorld * 2.3 - uTime * 0.4) * 0.4;
        // A surge line that washes out and back
        float surge = 0.5 + 0.5 * sin(uTime * 1.3 + (vWorld.x + vWorld.y) * 0.15);
        float band = 1.0 - smoothstep(0.15 + surge * 0.35, 0.95, vEdge + (n - 0.5) * 0.5);
        float lace = smoothstep(0.45, 0.7, n) * (1.0 - vEdge);
        float a = clamp(band * 0.85 + lace * 0.4, 0.0, 0.9);
        gl_FragColor = vec4(vec3(0.97, 0.99, 1.0), a);
        #include <colorspace_fragment>
      }
    `
  });
  return foamMaterial;
}

// A foam band hugging a w×d rectangle, spreading `spread` metres out into the water.
export function foamFrame(w, d, spread = 1.6, inset = 0.2) {
  const positions = [];
  const edge = [];
  const indices = [];
  // Walk the rectangle's perimeter as a loop of inner/outer pairs, rounding the corners
  const loop = [];
  const hw = w / 2 - inset;
  const hd = d / 2 - inset;
  const corners = [[hw, hd, 0], [-hw, hd, Math.PI / 2], [-hw, -hd, Math.PI], [hw, -hd, Math.PI * 1.5]];
  for (const [cx, cz, start] of corners) {
    for (let i = 0; i <= 4; i++) {
      const a = start + (i / 4) * (Math.PI / 2);
      loop.push([cx, cz, Math.cos(a), Math.sin(a)]);
    }
  }
  const outer = spread + inset;
  loop.forEach(([cx, cz, nx, nz], i) => {
    positions.push(cx, 0, cz, cx + nx * outer, 0, cz + nz * outer);
    edge.push(0, 1);
    const k = i * 2;
    const n = ((i + 1) % loop.length) * 2;
    indices.push(k, n, k + 1, k + 1, n, n + 1);
  });
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('aEdge', new THREE.Float32BufferAttribute(edge, 1));
  geometry.setIndex(indices);
  return geometry;
}

// A foam band around a circle of radius r.
export function foamRing(r, spread = 1.4, segments = 40) {
  const positions = [];
  const edge = [];
  const indices = [];
  for (let i = 0; i < segments; i++) {
    const a = (i / segments) * Math.PI * 2;
    const c = Math.cos(a);
    const s = Math.sin(a);
    positions.push(c * r, 0, s * r, c * (r + spread), 0, s * (r + spread));
    edge.push(0, 1);
    const k = i * 2;
    const n = ((i + 1) % segments) * 2;
    indices.push(k, n, k + 1, k + 1, n, n + 1);
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('aEdge', new THREE.Float32BufferAttribute(edge, 1));
  geometry.setIndex(indices);
  return geometry;
}

// A foam mesh that never fades with its obstacle (it is the sea, not the object)
export function foamMesh(geometry, y = 0.04) {
  const mesh = new THREE.Mesh(geometry, getFoamMaterial());
  mesh.position.y = y;
  mesh.userData.noFade = true;
  mesh.renderOrder = 1;
  return mesh;
}

// A leaning palm: a curved, segmented trunk and a crown of drooping fronds.
// Returns vertex-coloured parts in local space with the root at the origin.
export function palmParts(rand, { height = 6, lean = 0.35, x = 0, z = 0, y = 0, ry = 0 } = {}) {
  const parts = [];
  const segments = 6;
  let px = 0;
  let py = 0;
  const step = height / segments;
  for (let i = 0; i < segments; i++) {
    const t = i / segments;
    const tilt = lean * (0.3 + t * 0.9);
    const r = 0.26 - t * 0.1;
    const seg = new THREE.CylinderGeometry(r * 0.85, r, step * 1.05, 6);
    seg.translate(0, step / 2, 0);
    seg.rotateZ(-tilt);
    seg.translate(px, py, 0);
    parts.push(colored(seg, i % 2 ? SEA.palmTrunk : 0x87663f));
    px += Math.sin(tilt) * step;
    py += Math.cos(tilt) * step;
  }
  // Fronds: long flattened, drooping cones radiating from the crown
  const fronds = 7;
  for (let i = 0; i < fronds; i++) {
    const a = (i / fronds) * Math.PI * 2 + rand() * 0.4;
    const length = 2.6 + rand() * 0.8;
    const frond = new THREE.ConeGeometry(0.42, length, 4, 1);
    frond.translate(0, length / 2, 0);
    frond.scale(1, 1, 0.25);
    frond.rotateZ(-Math.PI / 2 - 0.3 + rand() * 0.45);
    frond.rotateY(a);
    frond.translate(px, py, 0);
    parts.push(colored(frond, rand() < 0.5 ? SEA.palmLeaf : SEA.palmLeafDark));
  }
  // Coconuts
  for (let i = 0; i < 3; i++) {
    parts.push(colored(new THREE.IcosahedronGeometry(0.17, 0), 0x6b4a2a, { x: px + Math.cos(i * 2.1) * 0.2, y: py - 0.2, z: Math.sin(i * 2.1) * 0.2 }));
  }
  return parts.map(g => g.rotateY(ry).translate(x, y, z));
}

// A rounded rock, jittered so no two are alike.
export function rockGeometry(rand, radius, color, { x = 0, y = 0, z = 0, squash = 0.7 } = {}) {
  return colored(jitter(new THREE.DodecahedronGeometry(radius, 0), radius * 0.35, rand), color, {
    x, y, z, scale: [1, squash, 1], ry: rand() * Math.PI
  });
}

// A floating marker buoy: a float, a striped spar and a cap.
export function buoyParts(color, { x = 0, y = 0, z = 0, height = 1.2 } = {}) {
  return [
    colored(new THREE.CylinderGeometry(0.42, 0.32, 0.45, 8), color, { x, y: y + 0.12, z }),
    colored(new THREE.CylinderGeometry(0.44, 0.44, 0.1, 8), SEA.white, { x, y: y + 0.3, z }),
    colored(new THREE.CylinderGeometry(0.1, 0.16, height, 6), color, { x, y: y + 0.35 + height / 2, z }),
    colored(new THREE.CylinderGeometry(0.17, 0.17, height * 0.22, 6), SEA.white, { x, y: y + 0.35 + height * 0.55, z }),
    colored(new THREE.ConeGeometry(0.2, 0.3, 6), color, { x, y: y + 0.5 + height, z })
  ];
}

// A rope between two points, as a thin sagging chain of segments.
export function ropeParts(a, b, { sag = 0.4, radius = 0.04, color = SEA.rope, segments = 4 } = {}) {
  const parts = [];
  const point = t => new THREE.Vector3(
    a.x + (b.x - a.x) * t,
    a.y + (b.y - a.y) * t - Math.sin(t * Math.PI) * sag,
    a.z + (b.z - a.z) * t
  );
  for (let i = 0; i < segments; i++) {
    const p0 = point(i / segments);
    const p1 = point((i + 1) / segments);
    const dir = new THREE.Vector3().subVectors(p1, p0);
    const len = dir.length();
    const geo = colored(new THREE.CylinderGeometry(radius, radius, len, 4), color);
    geo.applyQuaternion(new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir.normalize()));
    const mid = p0.add(p1).multiplyScalar(0.5);
    parts.push(geo.translate(mid.x, mid.y, mid.z));
  }
  return parts;
}
